import { createAsyncThunk } from "@reduxjs/toolkit"
import { APP_URL } from "../constants/app-url"
import { coreActions } from "../slices/core.slice"
import { jwtDecode } from "jwt-decode"
import {
  getFreshToken,
  runTimeoutForRefreshToken,
  clearStorageData,
  logoutFromIframes
} from "../utils/auth"
import { unregisterAIPanelUI } from "../utils/ai-panel"
import { resetAnalyticsIdentity } from "../utils/analytics"
import { hideShellLoader, redirectToLogin } from "../utils/dom"
import {
  isValidToken,
  getCookieValue,
  clearCookie,
  setCookie,
  getSessionInterval
} from "../utils/cookies"
import { fetchUserInfo } from "./user.thunks"
import { fetchProvisioningMessages } from "./provisioning.thunks"
import { tokenMonitor } from "../utils/token-monitor"
import { installApiInterceptor } from "../interceptors/api-interceptor"

const RELOAD_KEY = "coreui_local_auth_attempted"

export const autoLogin = createAsyncThunk(
  "core/autoLogin",
  async (_arg: any, { dispatch }) => {
    const authToken = getCookieValue("oidc.auth")
    const idToken = getCookieValue("oidc.id_token")
    const decodedUserToken = isValidToken(authToken)
      ? JSON.parse(atob(authToken))
      : null
    const decodedIdToken = isValidToken(idToken) ? jwtDecode(idToken) : null

    const hostname = APP_URL?.split(".")?.splice(1)?.join(".") || null

    if (decodedIdToken) {
      clearCookie("oidc.id_token", hostname)
      setCookie("oidc.id_token", idToken)
      dispatch(
        coreActions.setIdToken({
          idToken: decodedIdToken,
          idTokenEncoded: idToken
        })
      )
    }

    if (decodedUserToken) {
      clearCookie("oidc.auth", hostname)
      const expiry = new Date(decodedUserToken.expiry).toUTCString()
      setCookie("oidc.auth", authToken, expiry)
    }

    if (sessionStorage.getItem("cookieCheck")) {
      if (decodedUserToken) {
        const dateInterval = getSessionInterval(decodedUserToken)
        window.parent.postMessage(
          {
            message: "NEW_TOKEN_RECEIVED",
            dateInterval,
            decodedUserToken: decodedUserToken.token,
            decodedIdToken,
            idTokenEncoded: idToken
          },
          window.location.origin
        )
      } else {
        // Cookies are blocked by browser — attempt Storage Access API if available
        if (document.requestStorageAccess) {
          try {
            await document.requestStorageAccess()
            // If granted, retry reading cookies
            const retryAuthToken = getCookieValue("oidc.auth")
            const retryIdToken = getCookieValue("oidc.id_token")
            const retryDecodedUserToken = isValidToken(retryAuthToken)
              ? JSON.parse(atob(retryAuthToken))
              : null
            const retryDecodedIdToken = isValidToken(retryIdToken)
              ? jwtDecode(retryIdToken)
              : null

            if (retryDecodedUserToken) {
              const dateInterval = getSessionInterval(retryDecodedUserToken)
              window.parent.postMessage(
                {
                  message: "NEW_TOKEN_RECEIVED",
                  dateInterval,
                  decodedUserToken: retryDecodedUserToken.token,
                  decodedIdToken: retryDecodedIdToken,
                  idTokenEncoded: retryIdToken
                },
                window.location.origin
              )
              return
            }
          } catch (err) {
            console.warn("[coreui] Storage Access API request denied:", err)
          }
        }

        // Notify parent that cookies are blocked
        window.parent.postMessage(
          {
            message: "STORAGE_ACCESS_BLOCKED",
            error: "Third-party cookies are blocked by your browser"
          },
          window.location.origin
        )
      }
    }

    if (decodedUserToken) {
      sessionStorage.removeItem(RELOAD_KEY)
      const dateInterval = getSessionInterval(decodedUserToken)
      runTimeoutForRefreshToken(dateInterval)
      // TOKEN #1: Start continuous token monitoring (every 30 seconds)
      // alongside the single timeout, as a backup safety net. The expiry
      // the backend enforces is the `expiry` field inside the oidc.auth
      // cookie (base64 JSON) — the Cognito idToken's exp claim may have a
      // different TTL, so the monitor must NOT key off the idToken.
      let lastMonitorRefresh = 0
      tokenMonitor.startMonitoring(
        () => getCookieValue("oidc.auth") || null,
        () => {
          // Throttle to one attempt per minute so a failing refresh can't
          // spawn a new auth iframe on every 30-second check
          if (Date.now() - lastMonitorRefresh < 60000) return
          lastMonitorRefresh = Date.now()
          console.log("[autoLogin] Token monitor triggered refresh")
          getFreshToken()
        },
        {
          getExpiryTime: () => {
            try {
              const authCookie = getCookieValue("oidc.auth")
              if (!isValidToken(authCookie)) return null
              return new Date(JSON.parse(atob(authCookie)).expiry).getTime()
            } catch {
              return null
            }
          }
        }
      )
      // TOKEN #2: Install API interceptor to handle 401s with auto-retry
      installApiInterceptor()
      dispatch(coreActions.setUserToken(decodedUserToken.token))
      dispatch(fetchUserInfo(undefined))
      dispatch(fetchProvisioningMessages(undefined))
    } else if (window.location.hostname !== "localhost") {
      if (window.location.pathname !== "/logout") {
        redirectToLogin()
      } else {
        hideShellLoader()
        dispatch(coreActions.setLoaderStatus(false))
      }
    } else {
      dispatch(localAuthRequired(undefined))
    }
  }
)

export const appLogout = createAsyncThunk(
  "core/appLogout",
  async (_arg: any, { dispatch }) => {
    // Tear down the aria-ui panel BEFORE clearStorageData. clearStorageData
    // calls window.location.href = "/platform/logout?..." which navigates
    // the document away — once that's in flight the SPA is torn down by
    // the browser and aria-ui's unmount lifecycle (singleSpaCss <link>
    // removal, polling-loop teardown, etc.) never runs. Doing this first
    // means the link tag and any in-flight pollers are cleaned up properly
    // and the panel doesn't briefly flash an unauthenticated state.
    // Idempotent — no-op on tenants where AI was never enabled.
    try {
      await unregisterAIPanelUI()
    } catch (e) {
      console.error("Error unregistering aria-ui on logout: ", e)
    }
    // TOKEN #1: Stop token monitor on logout
    tokenMonitor.stopMonitoring()
    try {
      await logoutFromIframes()
    } catch (e) {
      console.error("Error logging out from iframes: ", e)
    }
    // FPN-6545: drop Gainsight's cached visitor identity before
    // clearStorageData navigates the document away, so the next login is a
    // fresh PX session instead of inheriting this user's identity. Must
    // stay ABOVE clearStorageData — once that runs, the page is leaving.
    resetAnalyticsIdentity()
    clearStorageData(true)
    hideShellLoader()
    dispatch(coreActions.resetOnLogout())
  }
)

export const localAuthRequired = createAsyncThunk(
  "core/localAuthRequired",
  async (_arg: any, { dispatch }) => {
    if (sessionStorage.getItem(RELOAD_KEY)) {
      console.warn(
        "[coreui] Already attempted local auth — stopping to prevent reload loop."
      )
      hideShellLoader()
      dispatch(coreActions.setLoaderStatus(false))
      return
    }

    console.log(
      "[coreui] Local auth required — fetching cookies from devtool..."
    )

    try {
      const res = await fetch("//localhost:9009/api/auth/cookies")
      if (!res.ok) throw new Error("Devtool returned " + res.status)
      const data = await res.json()

      if (data.authToken && data.cookiePrefix) {
        const prefix = data.cookiePrefix
        document.cookie = `${prefix}.cookie.oidc.auth=${data.authToken}; Path=/`
        document.cookie = `${prefix}.cookie.oidc.id_token=${
          data.idTokenValue || ""
        }; Path=/`
        sessionStorage.setItem(RELOAD_KEY, "true")
        window.location.reload()
      } else {
        console.warn(
          "[coreui] Devtool returned no tokens. Run the devtool and authenticate first."
        )
        hideShellLoader()
        dispatch(coreActions.setLoaderStatus(false))
      }
    } catch (err: any) {
      console.warn("[coreui] Could not reach devtool:", err.message)
      hideShellLoader()
      dispatch(coreActions.setLoaderStatus(false))
    }
  }
)
